import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, CalendarBlank, Buildings, CircleNotch } from '@phosphor-icons/react';
import { getMarketStatus } from '../utils/marketHours';
import { getUpcomingHolidays } from '../utils/marketHolidays';

// Metadatos de cada mercado (horario en su zona local).
const MARKETS = [
  {
    id: 'EU', key: 'europe', name: 'Europa', desc: 'BME · Xetra · Euronext',
    tz: 'Europe/Madrid', tzLabel: 'Hora de Madrid', accent: '#3b82f6',
    sessions: [
      { label: 'Pre-mercado', time: '08:00 – 09:00' },
      { label: 'Sesión regular', time: '09:00 – 17:30' },
      { label: 'Mercado extendido', time: '17:30 – 22:00' },
    ],
  },
  {
    id: 'US', key: 'us', name: 'Estados Unidos', desc: 'NYSE · NASDAQ',
    tz: 'America/New_York', tzLabel: 'Hora de Nueva York', accent: '#22c55e',
    sessions: [
      { label: 'Pre-mercado', time: '04:00 – 09:30' },
      { label: 'Sesión regular', time: '09:30 – 16:00' },
      { label: 'Post-mercado', time: '16:00 – 20:00' },
    ],
  },
  {
    id: 'MX', key: 'mexico', name: 'México', desc: 'BMV · Bolsa Mexicana de Valores',
    tz: 'America/Mexico_City', tzLabel: 'Hora de Ciudad de México', accent: '#e8c55c',
    sessions: [
      { label: 'Pre-apertura (subasta)', time: '08:00 – 08:30' },
      { label: 'Sesión regular', time: '08:30 – 15:00' },
      { label: 'Cierre (subasta)', time: '15:00 – 15:10' },
    ],
  },
];

const STATUS = {
  open: { label: 'Abierto', color: '#22c55e', bg: 'rgba(34,197,94,0.14)' },
  pre: { label: 'Pre-mercado', color: '#f59e0b', bg: 'rgba(245,158,11,0.14)' },
  post: { label: 'Post-mercado', color: '#60a5fa', bg: 'rgba(96,165,250,0.14)' },
  closed: { label: 'Cerrado', color: '#a1a1aa', bg: 'rgba(161,161,170,0.12)' },
};

// -----------------------------------------------------------------------------
// Conversión de horarios a HORA ESPAÑOLA (Europe/Madrid).
// Por petición del usuario, TODAS las franjas se muestran en hora de Madrid,
// convirtiendo desde la hora local de cada plaza. El desfase se calcula con la
// fecha actual, por lo que se ajusta automáticamente al horario de verano.
// -----------------------------------------------------------------------------
const MADRID_TZ = 'Europe/Madrid';

const tzOffsetMinutes = (tz) => {
  const now = new Date();
  const tzDate = new Date(now.toLocaleString('en-US', { timeZone: tz }));
  const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
  return Math.round((tzDate - utcDate) / 60000);
};

const toMadridHHMM = (hhmm, marketTz) => {
  const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
  const diff = tzOffsetMinutes(MADRID_TZ) - tzOffsetMinutes(marketTz);
  let total = h * 60 + m + diff;
  total = ((total % 1440) + 1440) % 1440;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(Math.floor(total / 60))}:${p(total % 60)}`;
};

const sessionInMadrid = (time, marketTz) => {
  if (marketTz === MADRID_TZ) return time;
  const [a, b] = time.split('–').map((s) => s.trim());
  if (!a || !b) return time;
  return `${toMadridHHMM(a, marketTz)} – ${toMadridHHMM(b, marketTz)}`;
};

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const WEEKDAYS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

const fmtHoliday = (key) => {
  const [y, m, d] = key.split('-').map((n) => parseInt(n, 10));
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${WEEKDAYS[wd]} ${d} ${MONTHS[m - 1]} ${y}`;
};

const localClock = (tz) => {
  try {
    return new Date().toLocaleTimeString('es-ES', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '--:--';
  }
};

const MarketCard = ({ market, phase, tick }) => {
  const st = STATUS[phase] || STATUS.closed;
  const holidays = useMemo(() => getUpcomingHolidays(market.id, 5), [market.id, tick > 0]);
  const clock = localClock(MADRID_TZ);

  return (
    <div
      className="rounded-2xl border p-5 flex flex-col gap-4 bg-gradient-to-b from-zinc-900/80 to-zinc-950/90"
      style={{ borderColor: `${market.accent}44`, boxShadow: `0 0 30px ${market.accent}18` }}
      data-testid={`market-calendar-card-${market.id}`}
    >
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${market.accent}1f`, border: `1px solid ${market.accent}55` }}>
            <Buildings size={20} weight="duotone" style={{ color: market.accent }} />
          </span>
          <div className="min-w-0">
            <div className="text-white font-black text-lg leading-tight">{market.name}</div>
            <div className="text-zinc-400 text-xs truncate">{market.desc}</div>
          </div>
        </div>
        <span
          className="px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider shrink-0 flex items-center gap-1.5"
          style={{ background: st.bg, color: st.color, border: `1px solid ${st.color}55` }}
          data-testid={`market-calendar-status-${market.id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color, boxShadow: phase !== 'closed' ? `0 0 8px ${st.color}` : 'none' }} />
          {st.label}
        </span>
      </div>

      {/* Reloj (hora española) */}
      <div className="flex items-center gap-2 text-sm">
        <Clock size={16} weight="bold" className="text-zinc-500" />
        <span className="font-mono font-bold text-white tabular-nums">{clock}</span>
        <span className="text-zinc-500 text-xs">· Hora española (Madrid)</span>
      </div>

      {/* Horarios (en hora española) */}
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Horario de sesión · hora española</div>
        {market.sessions.map((s) => (
          <div key={s.label} className="flex items-center justify-between text-[13px]">
            <span className="text-zinc-300">{s.label}</span>
            <span className="font-mono font-semibold text-white">{sessionInMadrid(s.time, market.tz)}</span>
          </div>
        ))}
      </div>

      {/* Próximos festivos */}
      <div className="space-y-1.5 pt-1 border-t border-zinc-800">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
          <CalendarBlank size={13} weight="bold" style={{ color: market.accent }} />
          Próximos festivos (sin sesión)
        </div>
        <div className="space-y-1">
          {holidays.map((h) => (
            <div key={h.date} className="flex items-center justify-between text-[12px]">
              <span className="text-zinc-400 font-mono">{fmtHoliday(h.date)}</span>
              <span className="text-zinc-200 font-medium text-right ml-3 truncate max-w-[55%]">{h.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MarketCalendarModal = ({ open, onClose }) => {
  const [tick, setTick] = useState(0);
  const status = useMemo(() => getMarketStatus(), [tick]);

  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="market-calendar-modal">
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-5xl rounded-3xl border border-gold-500/30 bg-zinc-950/95 shadow-2xl my-auto"
        style={{ boxShadow: '0 0 60px rgba(232,197,92,0.15), 0 30px 80px rgba(0,0,0,0.9)' }}
      >
        {/* Cabecera */}
        <div className="flex items-center justify-between gap-4 p-6 border-b border-zinc-800">
          <div>
            <div className="flex items-center gap-2.5">
              <CalendarBlank size={24} weight="duotone" className="text-gold-400" />
              <h2 className="text-white font-black text-xl sm:text-2xl">Calendario de Mercados</h2>
            </div>
            <p className="text-zinc-400 text-sm mt-1">Horarios de apertura, cierre, pre y post-mercado en hora española · Estado en tiempo real</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all shrink-0"
            data-testid="market-calendar-close"
            aria-label="Cerrar"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Resumen global */}
        <div className="px-6 pt-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-sm">
            <CircleNotch size={14} weight="bold" className={status.anyOpen ? 'text-emerald-400' : 'text-zinc-500'} />
            <span className="text-zinc-300">{status.label}</span>
          </div>
        </div>

        {/* Tarjetas de mercado */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
          {MARKETS.map((m) => (
            <MarketCard key={m.id} market={m} phase={status[m.key]} tick={tick} />
          ))}
        </div>

        <div className="px-6 pb-6 -mt-2">
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Todos los horarios se muestran en <strong className="text-zinc-300">hora española (Madrid)</strong> y se ajustan automáticamente al horario de verano.
            Los festivos se calculan según el calendario oficial de cada mercado. Información orientativa; puede variar en
            jornadas de media sesión o cambios de calendario.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MarketCalendarModal;
